import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import {
  PAYMENT_METHOD_TYPE,
  VERIFICATION_TYPES,
} from '@fantaz/shared/config/static';
import {
  CompletePaymentRequest,
  CompleteSessionDirectCashierResponse,
  DepositSessionRequest,
  ITransactionQueryParams,
  PaymentMethod,
  PaymentMethodFailureRequest,
  PaymentMethodWarningRequest,
  RequestWithdrawalResponse,
  SavePaymentMethodRequest,
  SavePaymentMethodResponse,
  StatusResponse,
  Transaction,
  TransactionListResponse,
  TsevoDirectCashierResponse,
  ValidateUserRequest,
  WithdrawPayload,
  WithdrawSessionRequest,
} from '@fantaz/shared/data-access/models';

import {
  PaymentSessionExpiredException,
  WalletService,
} from '../interfaces/wallet-service.interface';
import { FantazHttpClient } from '../utils/api-http.service';
import { ConfigApiService } from './config-api.service';
import { MockWalletApiService } from './mock-wallet-api.service';

@Injectable()
export class WalletApiService implements WalletService {
  http = inject(FantazHttpClient);
  configApiService = inject(ConfigApiService);

  getDepositSession(data: DepositSessionRequest): Observable<any> {
    if (!this.configApiService?.isTSEVODisabled()) {
      return this.http.post<any>(`/core-client/api/payment/deposit`, data).pipe(
        map((res) => {
          res.PaymentMethods = res?.PaymentMethods?.filter(
            (m: PaymentMethod) =>
              m.Type === PAYMENT_METHOD_TYPE.CC ||
              m.Type === PAYMENT_METHOD_TYPE.ACH,
          );
          return res;
        }),
      );
    }
    console.warn('endpoint "/core-client/api/payment/deposit" mocked');
    return MockWalletApiService.getPaymentSession();
  }

  completeDeposit(
    data: CompletePaymentRequest,
  ): Observable<CompleteSessionDirectCashierResponse> {
    return this.http
      .post<CompleteSessionDirectCashierResponse>(
        `/core-client/api/payment/complete-deposit`,
        data,
      )
      .pipe(
        map((res) => {
          if (res.status === 'TSEVO_ERROR') {
            throw new PaymentSessionExpiredException();
          }

          return res;
        }),
      );
  }

  preparePaymentMethod(data: SavePaymentMethodRequest) {
    return data.SavePaymentMethod
      ? data
      : {
          ...data,
          PaymentMethod: {
            Token: data.PaymentMethod.Token,
            Type: data.PaymentMethod.Type,
            NameOnAccount: data.PaymentMethod?.NameOnAccount,
            BillingAddress: data.PaymentMethod?.BillingAddress,
            ExpirationDate: data.PaymentMethod?.ExpirationDate,
            CVV: data.PaymentMethod.CVV,
          },
        };
  }

  savePaymentMethod(
    data: SavePaymentMethodRequest,
    GidxURL?: string,
  ): Observable<SavePaymentMethodResponse> {
    const endpoint = GidxURL || 'https://api.gidx-service.com/v3.0/api/';
    if (!this.configApiService.isTSEVODisabled()) {
      let payload = this.preparePaymentMethod(data);
      return this.http.post<SavePaymentMethodResponse>(
        new URL(`${endpoint}DirectCashier/PaymentMethod`),
        payload,
      );
    }
    console.warn(`endpoint "${endpoint}DirectCashier/PaymentMethod" mocked`);
    return MockWalletApiService.savePaymentMethodResponse();
  }

  requestToWithdraw(amount: number): Observable<RequestWithdrawalResponse> {
    return this.http.get<RequestWithdrawalResponse>(
      `/api/core/client/payment/withdraw/request?amount=${amount}`,
    );
  }

  getUserTransactions(
    payload: ITransactionQueryParams,
  ): Observable<TransactionListResponse> {
    let params = new HttpParams();
    Object.keys(payload).forEach((key) => {
      const value = payload[key as keyof ITransactionQueryParams];
      if (value) {
        params = params.append(key, value);
      }
    });
    return this.http.get<TransactionListResponse>(
      `/core-client/api/wallet-transactions`,
      { params },
    );
  }

  cancelWithdrawal(payload: WithdrawPayload): Observable<Transaction> {
    return this.http.post<Transaction>(`/core-client/api/cancel-withdrawal`, {
      ...payload,
    });
  }

  validateUserWithPaymentMethod(options: ValidateUserRequest): Observable<{
    status: keyof typeof VERIFICATION_TYPES;
  }> {
    if (!this.configApiService.isTSEVODisabled()) {
      return this.http.post<{ status: keyof typeof VERIFICATION_TYPES }>(
        `/core-client/api/payment/validate-card`,
        {
          ...options,
        },
      );
    }
    console.warn('endpoint "/core-client/api/payment/validate-card" mocked');
    return MockWalletApiService.validateByCardResponse();
  }

  registerCardValidationAttempt() {
    return this.http.get<StatusResponse>(
      `/core-client/api/user/card-verification-failed`,
    );
  }

  registerPaymentMethodFailure(payment: PaymentMethodFailureRequest) {
    return this.http.post<StatusResponse>(
      `/core-client/api/user/payment-method-failed`,
      {
        ...payment,
      },
    );
  }

  registerPaymentMethodWarning(payment: PaymentMethodWarningRequest) {
    return this.http.post<StatusResponse>(
      `/core-client/api/user/payment-method-warning`,
      {
        ...payment,
      },
    );
  }

  getWithdrawSession(
    data: WithdrawSessionRequest,
  ): Observable<TsevoDirectCashierResponse> {
    if (!this.configApiService?.isTSEVODisabled()) {
      return this.http.post<any>(`/core-client/api/payment/payout`, data).pipe(
        map((res) => {
          res.PaymentMethods = res?.PaymentMethods?.filter(
            (m: PaymentMethod) => m.Type === PAYMENT_METHOD_TYPE.ACH,
          );
          return res;
        }),
      );
    }
    console.warn('endpoint "/core-client/api/payment/payout" mocked');
    return MockWalletApiService.getPaymentSession();
  }

  completeWithdrawal(
    data: CompletePaymentRequest,
  ): Observable<CompleteSessionDirectCashierResponse> {
    if (!this.configApiService.isTSEVODisabled()) {
      return this.http.post<CompleteSessionDirectCashierResponse>(
        `/core-client/api/payment/complete-payout`,
        data,
      );
    }
    console.warn('endpoint "/core-client/api/payment/complete-payout" mocked');
    return MockWalletApiService.completeWithdrawalResponse();
  }
}
